export type TerminalInputToken =
	| { type: "insert"; text: string }
	| { type: "enter" }
	| { type: "backspace" }
	| { type: "delete" }
	| { type: "cursorLeft" }
	| { type: "cursorRight" }
	| { type: "cursorWordLeft" }
	| { type: "cursorWordRight" }
	| { type: "historyPrevious" }
	| { type: "historyNext" }
	| { type: "lineStart" }
	| { type: "lineEnd" }
	| { type: "killToStart" }
	| { type: "killToEnd" }
	| { type: "deleteWordBefore" }
	| { type: "interrupt" }
	| { type: "clearScreen" }
	| { type: "complete" };

type ParserState = "normal" | "escape" | "csi" | "ss3" | "osc" | "oscEscape" | "paste";

const BRACKETED_PASTE_END = "\x1b[201~";
const MAX_CSI_SEQUENCE_LENGTH = 32;

const CONTROL_TOKENS: Readonly<Record<string, TerminalInputToken["type"]>> = {
	"\x01": "lineStart",
	"\x02": "cursorLeft",
	"\x03": "interrupt",
	"\x04": "delete",
	"\x05": "lineEnd",
	"\x06": "cursorRight",
	"\x0b": "killToEnd",
	"\x0c": "clearScreen",
	"\x15": "killToStart",
	"\x17": "deleteWordBefore",
};

const CSI_TOKENS: Readonly<Record<string, TerminalInputToken["type"]>> = {
	A: "historyPrevious",
	B: "historyNext",
	C: "cursorRight",
	D: "cursorLeft",
	H: "lineStart",
	F: "lineEnd",
	"1~": "lineStart",
	"3~": "delete",
	"4~": "lineEnd",
	"7~": "lineStart",
	"8~": "lineEnd",
	"1;3C": "cursorWordRight", // option+→ (xterm.js)
	"1;3D": "cursorWordLeft", // option+←
	"1;5C": "cursorWordRight", // ctrl+→
	"1;5D": "cursorWordLeft", // ctrl+←
};

const SS3_TOKENS: Readonly<Record<string, TerminalInputToken["type"]>> = {
	A: "historyPrevious",
	B: "historyNext",
	C: "cursorRight",
	D: "cursorLeft",
	H: "lineStart",
	F: "lineEnd",
};

function isCsiFinal(character: string): boolean {
	const code = character.charCodeAt(0);
	return code >= 0x40 && code <= 0x7e;
}

function sanitizePasteText(text: string): string {
	let result = "";
	for (const character of text) {
		if (character === "\r" || character === "\n") result += "\n";
		else if (character === "\t" || character.charCodeAt(0) >= 0x20) result += character;
	}
	return result;
}

/** xtermのonDataストリームをライン編集操作へ変換する。 */
export class TerminalInputParser {
	private state: ParserState = "normal";
	private sequence = "";
	private pasteEndCandidate = "";

	feed(data: string): TerminalInputToken[] {
		const tokens: TerminalInputToken[] = [];
		let insertedText = "";

		const flushInsert = (): void => {
			if (!insertedText) return;
			tokens.push({ type: "insert", text: insertedText });
			insertedText = "";
		};
		const emit = (type: TerminalInputToken["type"]): void => {
			flushInsert();
			tokens.push({ type } as Exclude<TerminalInputToken, { type: "insert" }>);
		};

		for (const character of data) {
			if (this.state === "paste") {
				if (this.pasteEndCandidate || character === "\x1b") {
					this.pasteEndCandidate += character;
					if (BRACKETED_PASTE_END.startsWith(this.pasteEndCandidate)) {
						if (this.pasteEndCandidate === BRACKETED_PASTE_END) {
							flushInsert();
							this.pasteEndCandidate = "";
							this.state = "normal";
						}
						continue;
					}
					// 不一致のESC自体が終端開始の可能性があるため、末尾のESCだけを有界に保持する。
					if (character === "\x1b") {
						insertedText += sanitizePasteText(this.pasteEndCandidate.slice(0, -1));
						this.pasteEndCandidate = "\x1b";
					} else {
						insertedText += sanitizePasteText(this.pasteEndCandidate);
						this.pasteEndCandidate = "";
					}
					continue;
				}
				insertedText += sanitizePasteText(character);
				continue;
			}

			if (this.state === "osc") {
				if (character === "\x07") this.state = "normal";
				else if (character === "\x1b") this.state = "oscEscape";
				continue;
			}
			if (this.state === "oscEscape") {
				this.state = character === "\\" ? "normal" : character === "\x1b" ? "oscEscape" : "osc";
				continue;
			}
			if (this.state === "escape") {
				if (character === "[") {
					this.state = "csi";
					this.sequence = "";
				} else if (character === "O") {
					this.state = "ss3";
				} else if (character === "]") {
					this.state = "osc";
				} else if (character === "b" || character === "f") {
					// ESC b / ESC f を送るターミナル向けの単語移動。
					this.state = "normal";
					emit(character === "b" ? "cursorWordLeft" : "cursorWordRight");
				} else {
					this.state = "normal";
				}
				continue;
			}
			if (this.state === "ss3") {
				const token = SS3_TOKENS[character];
				this.state = "normal";
				if (token) emit(token);
				continue;
			}
			if (this.state === "csi") {
				if (this.sequence.length < MAX_CSI_SEQUENCE_LENGTH) this.sequence += character;
				if (!isCsiFinal(character)) continue;

				const sequence = this.sequence;
				this.sequence = "";
				this.state = "normal";
				if (sequence === "200~") {
					flushInsert();
					this.state = "paste";
				} else {
					const token = CSI_TOKENS[sequence];
					if (token) emit(token);
				}
				continue;
			}

			if (character === "\x1b") {
				this.state = "escape";
				continue;
			}
			if (character === "\r") {
				emit("enter");
				continue;
			}
			if (character === "\x7f" || character === "\x08") {
				emit("backspace");
				continue;
			}
			const controlToken = CONTROL_TOKENS[character];
			if (controlToken) {
				emit(controlToken);
				continue;
			}
			// 生のタブは補完要求。ペースト内のタブは sanitizePasteText 側で文字として残す。
			if (character === "\t") {
				emit("complete");
				continue;
			}
			if (character.charCodeAt(0) >= 0x20) insertedText += character;
		}

		flushInsert();
		return tokens;
	}

	reset(): void {
		this.state = "normal";
		this.sequence = "";
		this.pasteEndCandidate = "";
	}
}
